/**
 * HRPersons — Vue dédiée "Personnes → Mandataires & Externes".
 * Sépare les non-salariés (dirigeants, prestataires, conducteurs occasionnels)
 * de la vue Salariés classique. Utile pour la RH qui ne les mélange plus
 * dans l'effectif, RUP, compliance.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Users, Briefcase, UserPlus, ShieldCheck, ShieldAlert, MapPin,
  Mail, Phone, Loader2, UserCog, Trash2, Save, Info,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  mandataire_social: {
    label: "Mandataires sociaux",
    hint: "Dirigeants sans contrat de travail (gérants SARL, PDG SAS, présidents…). Exclus de l'effectif, du RUP et des rappels compliance.",
    cls: "bg-violet-50 text-violet-700 border-violet-200",
    icon: Briefcase,
  },
  conducteur_externe: {
    label: "Conducteurs externes",
    hint: "Prestataires, famille du dirigeant, stagiaires ponctuels — tracés uniquement pour les affectations de véhicules et amendes.",
    cls: "bg-orange-50 text-orange-700 border-orange-200",
    icon: Users,
  },
};

export default function HRPersons() {
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState({
    mandataire_social: [], conducteur_externe: [], employee: [],
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [createType, setCreateType] = useState("mandataire_social");
  const [editingDriver, setEditingDriver] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/fleet/drivers");
      setDrivers(r.data.groups || {});
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return drivers;
    const filt = (arr) => arr.filter(d =>
      `${d.first_name} ${d.last_name}`.toLowerCase().includes(s) ||
      (d.email || "").toLowerCase().includes(s) ||
      (d.job_title || "").toLowerCase().includes(s)
    );
    return {
      mandataire_social: filt(drivers.mandataire_social || []),
      conducteur_externe: filt(drivers.conducteur_externe || []),
      employee: filt(drivers.employee || []),
    };
  }, [drivers, search]);

  const totalNonEmp = (drivers.mandataire_social?.length || 0)
                    + (drivers.conducteur_externe?.length || 0);

  return (
    <div className="space-y-6" data-testid="hr-persons-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            <Users className="w-6 h-6 text-slate-700" />
            Personnes non salariées
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Mandataires sociaux & conducteurs externes — exclus de l'effectif salarié, du RUP et de la paie
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Input placeholder="Rechercher…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-48" data-testid="persons-search" />
          <Button variant="outline" onClick={() => { setCreateType("mandataire_social"); setCreateOpen(true); }}
            data-testid="persons-create-mandataire">
            <Briefcase className="w-4 h-4 mr-1.5" /> Mandataire
          </Button>
          <Button onClick={() => { setCreateType("conducteur_externe"); setCreateOpen(true); }}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="persons-create-externe">
            <UserPlus className="w-4 h-4 mr-1.5" /> Conducteur externe
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) =>
            <div key={i} className="h-24 bg-slate-100 rounded animate-pulse" />)}
        </div>
      ) : totalNonEmp === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">
            Aucune personne non-salariée enregistrée.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Crée un mandataire (gérant, PDG…) ou un conducteur externe pour affecter des véhicules hors payroll.
          </p>
        </CardContent></Card>
      ) : (
        <>
          {["mandataire_social", "conducteur_externe"].map(t => {
            const meta = TYPE_META[t]; const Icon = meta.icon;
            const list = filtered[t] || [];
            if (list.length === 0 && drivers[t]?.length === 0) return null;
            return (
              <motion.div key={t} initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.cls}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      {meta.label}
                      <Badge variant="outline" className="text-[10px]">
                        {drivers[t]?.length || 0}
                      </Badge>
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">{meta.hint}</p>
                  </CardHeader>
                  <CardContent className="p-0">
                    {list.length === 0 ? (
                      <div className="py-6 text-center text-xs text-slate-400">
                        Aucun résultat pour cette recherche.
                      </div>
                    ) : (
                      <div className="divide-y">
                        {list.map(d => (
                          <button key={d.id}
                            onClick={() => setEditingDriver(d)}
                            className="w-full grid grid-cols-12 items-center gap-2 p-3 hover:bg-slate-50 text-left"
                            data-testid={`person-row-${d.id}`}>
                            <div className="col-span-4">
                              <div className="font-semibold text-slate-900 text-sm">
                                {d.first_name} {d.last_name}
                              </div>
                              {d.job_title && (
                                <div className="text-[11px] text-slate-500">{d.job_title}</div>
                              )}
                            </div>
                            <div className="col-span-4 text-xs text-slate-600">
                              {d.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {d.email}</div>}
                            </div>
                            <div className="col-span-4 flex items-center justify-end gap-2">
                              {d.has_license ? (
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                                  <ShieldCheck className="w-3 h-3 mr-1" /> Permis
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                                  <ShieldAlert className="w-3 h-3 mr-1" /> Permis manquant
                                </Badge>
                              )}
                              {d.has_address && (
                                <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px]">
                                  <MapPin className="w-3 h-3 mr-1" /> Adresse
                                </Badge>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </>
      )}

      <CreatePersonDialog open={createOpen}
        onOpenChange={setCreateOpen}
        defaultType={createType}
        onCreated={() => { setCreateOpen(false); load(); }} />
      <EditPersonDialog driver={editingDriver}
        onOpenChange={(v) => !v && setEditingDriver(null)}
        onSaved={load} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function CreatePersonDialog({ open, onOpenChange, defaultType, onCreated }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [personType, setPersonType] = useState(defaultType);
  const [email, setEmail] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setFirstName(""); setLastName(""); setEmail("");
      setJobTitle(""); setPersonType(defaultType); }
  }, [open, defaultType]);

  const submit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Nom et prénom requis"); return;
    }
    setBusy(true);
    try {
      await api.post("/hr/drivers/quick-create", {
        first_name: firstName.trim(), last_name: lastName.trim(),
        person_type: personType,
        email: email.trim() || null,
        job_title: jobTitle.trim() || null,
      });
      toast.success(`${firstName} ${lastName} créé(e)`);
      onCreated?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Nouvelle {personType === "mandataire_social" ? "personne dirigeante" : "personne externe"}
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setPersonType("mandataire_social")}
              className={`px-3 py-2 rounded-md border text-left ${
                personType === "mandataire_social"
                  ? "bg-violet-50 border-violet-300 text-violet-800"
                  : "bg-white border-slate-200 hover:border-slate-300"}`}>
              <div className="text-xs font-semibold flex items-center gap-1">
                <Briefcase className="w-3 h-3" /> Mandataire social
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">Gérant, PDG…</div>
            </button>
            <button type="button" onClick={() => setPersonType("conducteur_externe")}
              className={`px-3 py-2 rounded-md border text-left ${
                personType === "conducteur_externe"
                  ? "bg-orange-50 border-orange-300 text-orange-800"
                  : "bg-white border-slate-200 hover:border-slate-300"}`}>
              <div className="text-xs font-semibold flex items-center gap-1">
                <Users className="w-3 h-3" /> Externe
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">Prestataire, famille…</div>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase text-slate-500">Prénom *</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                autoFocus data-testid="create-person-first-name" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Nom *</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)}
                data-testid="create-person-last-name" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="create-person-email" />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Fonction</Label>
            <Input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Ex : Gérant" data-testid="create-person-job" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={busy}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="create-person-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                     : <UserPlus className="w-4 h-4 mr-2" />}
            Créer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
function EditPersonDialog({ driver, onOpenChange, onSaved }) {
  const [full, setFull] = useState(null);
  const [license, setLicense] = useState("");
  const [licenseDate, setLicenseDate] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthPlace, setBirthPlace] = useState("");
  const [street, setStreet] = useState("");
  const [postal, setPostal] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!driver) { setFull(null); return; }
    (async () => {
      try {
        const r = await api.get(`/hr/employees/${driver.id}`);
        const e = r.data.employee;
        setFull(e);
        setLicense(e.driver_license_number || "");
        setLicenseDate(e.driver_license_issued_at || "");
        setBirthDate(e.birth_date || "");
        setBirthPlace(e.birth_place || "");
        const addr = e.address || {};
        setStreet(addr.street || "");
        setPostal(addr.postal_code || "");
        setCity(addr.city || "");
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, [driver]);

  const save = async () => {
    if (!full) return;
    setBusy(true);
    try {
      await api.patch(`/hr/employees/${full.id}/driver-info`, {
        driver_license_number: license || null,
        driver_license_issued_at: licenseDate || null,
        birth_date: birthDate || null,
        birth_place: birthPlace || null,
        address: { street: street || null, postal_code: postal || null, city: city || null },
      });
      toast.success("Personne mise à jour");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!full) return;
    if (!confirm(`Supprimer ${full.first_name} ${full.last_name} ? Cette action est réversible via la corbeille RH.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/hr/employees/${full.id}`);
      toast.success("Personne supprimée");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setDeleting(false); }
  };

  if (!driver) return null;

  return (
    <Dialog open={!!driver} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="w-4 h-4" /> {driver.first_name} {driver.last_name}
            <Badge variant="outline" className={TYPE_META[driver.person_type]?.cls}>
              {TYPE_META[driver.person_type]?.label}
            </Badge>
          </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        {!full ? (
          <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3 text-xs text-indigo-900 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>Ces informations pré-remplissent automatiquement le Cerfa 15495 et l'export CSV ANTAI. Modifiable côté RH ou Parc Auto — 1 seule source.</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase text-slate-500">N° permis</Label>
                <Input value={license} onChange={(e) => setLicense(e.target.value)}
                  data-testid="edit-person-license" />
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-500">Délivrance</Label>
                <Input type="date" value={licenseDate}
                  onChange={(e) => setLicenseDate(e.target.value)}
                  data-testid="edit-person-license-date" />
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-500">Naissance</Label>
                <Input type="date" value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  data-testid="edit-person-birth-date" />
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-500">Lieu naissance</Label>
                <Input value={birthPlace}
                  onChange={(e) => setBirthPlace(e.target.value)}
                  data-testid="edit-person-birth-place" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Adresse</Label>
              <Input value={street} onChange={(e) => setStreet(e.target.value)}
                placeholder="14 rue des Lilas" className="mb-2"
                data-testid="edit-person-street" />
              <div className="grid grid-cols-3 gap-2">
                <Input value={postal} onChange={(e) => setPostal(e.target.value)}
                  placeholder="75011" data-testid="edit-person-postal" />
                <Input value={city} onChange={(e) => setCity(e.target.value)}
                  placeholder="Paris" className="col-span-2"
                  data-testid="edit-person-city" />
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="ghost" onClick={del} disabled={deleting || !full}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 mr-auto"
            data-testid="edit-person-delete">
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
          <Button onClick={save} disabled={busy || !full}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="edit-person-save">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                     : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
